import React from 'react';
import { ApplicationView, useController } from '@aha-app/mvc';
import styles from './ConversationPanel.module.css';
import MessageContent from './MessageContent';
import type AssistantMessageModel from '../../../framework/models/AssistantMessage';
import type AssistantViewController from '../AssistantView/AssistantViewController';
import classNames from 'classnames';
import Reasoning from './Reasoning';
import ToolCall from './ToolCall';
import { FROZEN_CHAT_ERROR } from 'components/Assistant/constants';

interface AssistantMesssageProps {
  message: AssistantMessageModel;
}

const SafeWrapper: React.FC<{
  message: AssistantMessageModel;
  children: React.ReactNode;
}> = ApplicationView(({ message, children }) => {
  const [show, setShow] = React.useState(false);

  if (message.safe || show) {
    return <>{children}</>;
  } else {
    return (
      <div className={styles.assistantMessageUnsafeContainer}>
        <div className={styles.assistantMessageUnsafe} aria-hidden>
          {children}
        </div>
        <div className={styles.assistantMessageUnsafeOverlay}>
          <div className={styles.assistantMessageUnsafeMessage}>
            This message may contain unsafe content{' '}
            <aha-button size='mini' onClick={() => setShow(true)}>
              Show anyway
            </aha-button>
          </div>
        </div>
      </div>
    );
  }
});

const AssistantMessage: React.FC<AssistantMesssageProps> = ({
  message,
}: AssistantMesssageProps) => {
  const controller = useController<AssistantViewController>();
  const { readOnly } = controller.state;

  const renderToolCalls = () => {
    if (!message.toolCalls || message.toolCalls.length === 0) return null;

    const toolCalls = message.toolCalls.map((toolCall, idx) => (
      <ToolCall key={toolCall.id} toolCall={toolCall} isGrouped={false} />
    ));

    const toolCallsPendingApproval = message.toolCalls.filter(
      toolCall => toolCall.status === 'needs_approval'
    );

    let approvalAllButtons: React.ReactElement | null = null;
    if (!readOnly && toolCallsPendingApproval.length > 1) {
      approvalAllButtons = (
        <div className={styles.bulkToolCallActions}>
          <aha-button
            kind='plain'
            size='mini'
            {...(controller.model.isChatFrozen ? { disabled: true } : {})}
            onClick={() =>
              controller.actionRejectAllToolCalls(toolCallsPendingApproval)
            }
          >
            Ignore
          </aha-button>
          {controller.model.isChatFrozen && (
            <aha-tooltip>{FROZEN_CHAT_ERROR}</aha-tooltip>
          )}
          <aha-button
            size='mini'
            kind='primary'
            {...(controller.model.isChatFrozen ? { disabled: true } : {})}
            onClick={() =>
              controller.actionApproveAllToolCalls(
                toolCallsPendingApproval.map(tc => ({ toolCall: tc }))
              )
            }
          >
            Approve all
          </aha-button>
          {controller.model.isChatFrozen && (
            <aha-tooltip>{FROZEN_CHAT_ERROR}</aha-tooltip>
          )}
        </div>
      );
    }

    if (message.toolCalls.length > 1) {
      return (
        <div className={styles.bulkToolCalls}>
          {toolCalls.map((toolCall, idx) => (
            <div key={idx} className={styles.toolCallRow}>
              {toolCall}
            </div>
          ))}
          {approvalAllButtons}
        </div>
      );
    } else {
      return toolCalls;
    }
  };

  /*
  const artifactCandidate =
    message.content.length > 500 || message.content.split('\n').length > 4;

    <aha-button
          kind='icon'
          size='mini'
          onClick={() => model.convertToArtifact(message)}
        >
          <i className='fa-regular fa-file-lines' />
          <aha-tooltip style={{ '--Tooltip__tooltip--min-width': 0 }}>
            Convert to an artifact
          </aha-tooltip>
        </aha-button>
  */

  const buttons = (
    <div className={styles.assistantMessageButtons}>
      <aha-copy-button size='mini' text={message.content} />
    </div>
  );

  return (
    <div
      className={classNames(styles.assistantMessage, {
        [styles.assistantMessageWithContent]: message.content?.length > 0,
      })}
      data-message-id={message.id}
    >
      <SafeWrapper message={message}>
        {!window.featureFlags?.a_18520_redesigned_tool_calls &&
          message.reasoning &&
          message.reasoning.length > 0 && (
            <Reasoning
              reasoning={message.reasoning}
              hasContent={
                !message.active ||
                message.content.length > 0 ||
                message.toolCalls.length > 0
              }
            />
          )}
        {message.content && (
          <div>
            <MessageContent
              body={message.content}
              contentType={'text/markdown'}
            />
            {!message.active && message.content.length > 0 && buttons}
          </div>
        )}
        {!window.featureFlags?.a_18520_redesigned_tool_calls &&
          renderToolCalls()}
      </SafeWrapper>
    </div>
  );
};

export default ApplicationView(AssistantMessage);
